import React, { Fragment, useContext, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import download from 'downloadjs';
import { useHistory } from 'react-router-dom';

import { GlobalContext } from '../../store/Provider';
import { setAlert } from '../../store/actions/alerts';
import { setLoading } from '../../store/actions/documents';
import PDFViewerModal from './PDFViewerModal';
import { fetchAndMapAnnotations } from '../../services/AnnotationService';

const DocumentItem = ({ document, annotations: annotationsProp = {} }) => {
  const { alertsDispatch, documentsDispatch } = useContext(GlobalContext);
  const [isPdfViewerOpen, setIsPdfViewerOpen] = useState(false);
  const [pdfData, setPdfData] = useState(null);
  const [currentAnnotations, setCurrentAnnotations] = useState([]);

  const { filename, filetype, field_desecnm, factuurvoorsteldatum, factuurvoorstelnummer } = document;

  const { t } = useTranslation();

  const history = useHistory();

  const onStatistics = () => {
    history.push(`/statistics?facvstnr=${factuurvoorstelnummer}`);
  };

  const base64toBlob = base64Data => {
    if (typeof base64Data !== 'string') {
      console.error(
        'DocumentItem: base64toBlob expected a string but received',
        typeof base64Data,
        base64Data && typeof base64Data === 'object'
          ? Object.keys(base64Data)
          : base64Data
      );
      throw new Error('Expected base64 string but received ' + typeof base64Data);
    }
    const sliceSize = 1024;
    // Strip an optional data URI prefix and any whitespace/newlines the backend may add
    const cleaned = base64Data.replace(/^data:[^;]*;base64,/, '').replace(/\s/g, '');
    let byteCharacters;
    try {
      byteCharacters = atob(cleaned);
    } catch (decodeErr) {
      console.error(
        'DocumentItem: atob failed. length:',
        cleaned.length,
        'starts with:',
        JSON.stringify(cleaned.slice(0, 80)),
        'ends with:',
        JSON.stringify(cleaned.slice(-20))
      );
      throw decodeErr;
    }
    const bytesLength = byteCharacters.length;
    const slicesCount = Math.ceil(bytesLength / sliceSize);
    const byteArrays = new Array(slicesCount);

    for (let sliceIndex = 0; sliceIndex < slicesCount; ++sliceIndex) {
      const begin = sliceIndex * sliceSize;
      const end = Math.min(begin + sliceSize, bytesLength);

      const bytes = new Array(end - begin);
      for (let offset = begin, i = 0; offset < end; ++i, ++offset) {
        bytes[i] = byteCharacters[offset].charCodeAt(0);
      }
      byteArrays[sliceIndex] = new Uint8Array(bytes);
    }
    return new Blob(byteArrays, { type: 'application/pdf' });
  };

    const onView = async () => {
       setLoading(true)(documentsDispatch);

       try {
         console.log('DocumentItem: Fetching document', filename, 'type:', filetype);
         const res = await axios.get(
           `/jsonapi/document?desecnm=${field_desecnm}&title=${filename}`
         );

         console.log('DocumentItem: Document fetched successfully, data type:', typeof res.data);
         if (res.data && typeof res.data === 'object') {
           console.log('DocumentItem: response keys:', Object.keys(res.data));
         }

         // Handle the new JSON structure with pdf and textPositions
         const pdfData = res.data.pdf || res.data;
         const textPositions = res.data.textPositions || {};
         const pageHeights = res.data.pageHeights || {};

         console.log('DocumentItem: PDF extracted, textPositions:', textPositions, 'pageHeights:', pageHeights);

         // Handle Afrekeningsborderel documents with inline viewer
         if (filetype === 'Afrekeningsborderel') {
           console.log('DocumentItem: Opening Afrekeningsborderel in inline viewer');
           setPdfData(pdfData);
           
           // Try to fetch dynamic annotations from webservice
           console.log('DocumentItem: Attempting to fetch dynamic annotations');
           console.log('  - field_desecnm:', field_desecnm);
           console.log('  - filename:', filename);
           console.log('  - factuurvoorstelnummer:', factuurvoorstelnummer);
           
           try {
             const dynamicAnnotations = await fetchAndMapAnnotations(field_desecnm, filename, factuurvoorstelnummer);
             
             console.log('DocumentItem: Annotation service returned:', dynamicAnnotations.length, 'annotations');
             
             if (dynamicAnnotations && dynamicAnnotations.length > 0) {
               console.log('DocumentItem: Using dynamic annotations from webservice:', dynamicAnnotations);
               // Update annotations with dynamic y positions if available from backend
                // Per-annotation Y offset corrections in pixels (positive = lower, negative = higher)
                const yOffsetCorrections = {
                  '1': 20,
                  '2': -20,
                  '3': -20,
                  '4': -5,
                  '6': -5,
                  '7': -20,
                  '8': -20,
                };

                const updatedAnnotations = dynamicAnnotations.map(annotation => {
                  // Check if textPositions has an entry for this annotation number
                  const annotationNumber = String(annotation.number);
                  if (textPositions[annotationNumber]) {
                    const position = textPositions[annotationNumber];
                    const pageNum = position.page || annotation.page;
                    const pageHeight = pageHeights[pageNum];
                    
                    // Per-annotation pixel offset applied after conversion (negative = higher on screen)
                    const yOffset = yOffsetCorrections[annotationNumber] || 0;

                    // Convert pixel coordinate to percentage (0-100)
                    let yPercentage = annotation.y; // default to existing value
                    if (pageHeight) {
                       // PDF coordinates have origin at bottom-left, CSS top is from top
                       yPercentage = ((pageHeight - position.y) / pageHeight) * 100;
                       // Apply pixel offset as percentage correction
                       yPercentage += (yOffset / pageHeight) * 100;
                   } else {
                     console.warn(`DocumentItem: No pageHeight found for page ${pageNum}, using raw y value`);
                     yPercentage = position.y;
                   }
                   
                   return { 
                     ...annotation, 
                     y: yPercentage,
                     page: pageNum
                   };
                 }
                 return annotation;
               });
               setCurrentAnnotations(updatedAnnotations);
             } else {
               console.log('DocumentItem: No dynamic annotations returned, using fallback');
               // Fallback to static annotations or test annotations
               let documentAnnotations = annotationsProp[filename] || [];
               
               if (documentAnnotations.length === 0) {
                 // TEST: Add default test annotations if none provided
                 documentAnnotations = [
                   {
                     id: 'test-ann-1',
                     x: 20,
                     y: 15,
                     text: 'This is a test annotation. Check the document carefully.',
                     title: 'Document Header',
                     type: 'info',
                     page: 1,
                     visible: true
                   },
                   {
                     id: 'test-ann-2',
                     x: 75,
                     y: 50,
                     text: 'Important section - verify all details before signing.',
                     title: 'Important Notice',
                     type: 'warning',
                     page: 1,
                     visible: true
                   },
                   {
                     id: 'test-ann-3',
                     x: 50,
                     y: 85,
                     text: 'Signature area - sign and date here',
                     title: 'Signature Required',
                     type: 'highlight',
                     page: 1,
                     visible: true
                   }
                 ];
                 console.log('DocumentItem: Using test annotations (no dynamic data)');
               }
               
               setCurrentAnnotations(documentAnnotations);
               console.log('DocumentItem: Annotations set:', documentAnnotations);
             }
          } catch (annotationErr) {
            console.warn('DocumentItem: Error fetching dynamic annotations:', annotationErr.message);
            
            // If 403 Forbidden, don't show any annotations
            if (annotationErr.message === 'FORBIDDEN') {
              console.warn('DocumentItem: Access denied - no annotations will be displayed');
              setCurrentAnnotations([]);
            } else {
              // Fallback to static annotations for other errors
              let documentAnnotations = annotationsProp[filename] || [];
              if (documentAnnotations.length === 0) {
                documentAnnotations = [
                  {
                    id: 'test-ann-1',
                    x: 20,
                    y: 15,
                    text: 'This is a test annotation. Check the document carefully.',
                    title: 'Document Header',
                    type: 'info',
                    page: 1,
                    visible: true
                  }
                ];
              }
              setCurrentAnnotations(documentAnnotations);
            }
          }
          
          setIsPdfViewerOpen(true);
        } else {
          // For other document types, open in new window
          console.log('DocumentItem: Opening document in new window');
          const blob = base64toBlob(pdfData);
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl);
        }
      } catch (err) {
        console.error('DocumentItem: Error fetching document:', err);
        if (navigator.onLine) {
          setAlert(
            t('alerts.errors.documents.couldNotRequestDocument'),
            'danger'
          )(alertsDispatch);
        } else {
          setAlert(
            t('alerts.errors.internetConnectionRequired'),
            'danger'
          )(alertsDispatch);
        }
      }

      setLoading(false)(documentsDispatch);
    };

  const onDownload = async () => {
    setLoading(true)(documentsDispatch);

    try {
      const res = await axios.get(
        `/jsonapi/document?desecnm=${field_desecnm}&title=${filename}`
      );

      // Handle the new JSON structure with pdf and textPositions
      const pdfData = res.data.pdf || res.data;

      if(filetype === 'Cercosoft') {
        
        //md5 hash
        const covFile = pdfData;
        download(atob(covFile), filename, 'application/cov');
      } else {
        const base64 = `data:application/pdf;base64,${pdfData}`;
        download(base64, filename, 'application/pdf');
      }
  


    } catch (err) {
      if (navigator.onLine) {
        console.log(err);
        setAlert(
          t('alerts.errors.documents.couldNotRequestDocument'),
          'danger'
        )(alertsDispatch);
      } else {
        setAlert(
          t('alerts.errors.internetConnectionRequired'),
          'danger'
        )(alertsDispatch);
      }
    }

    setLoading(false)(documentsDispatch);
  };
  const handleClosePdfViewer = () => {
    setIsPdfViewerOpen(false);
    setPdfData(null);
    setCurrentAnnotations([]);
  };

  const handleAnnotationClose = (annotationId) => {
    // Optionally handle annotation closure (e.g., log analytics)
    console.log(`Annotation ${annotationId} closed`);
  };

  return (
    <Fragment>
      {field_desecnm !== '' && filetype !== 'Cercosoft' ?  (
        <tr>
          <td>{filename}</td>
          <td>{filetype}</td>
          <td>{factuurvoorsteldatum}</td>
          <td>
            <button onClick={onView} className='btn btn-secondary btn-sm'>
              <i className='icon-eye'></i> <span>{t('general.view')}</span>
            </button>

            <button onClick={onDownload} className='btn btn-secondary btn-sm'>
              <i className='icon-cloud-download'></i>{' '}
              <span>{t('general.download')}</span>
            </button>

            {filetype === 'Afrekeningsborderel' && (
              <button onClick={onStatistics} className='btn btn-secondary btn-sm'>
                <i className='mdi mdi-chart-bar'></i>{' '}
                <span>{t('documents.statistics')}</span>
              </button>
            )}
          </td>
        </tr>
      ) : field_desecnm !== '' && filetype === 'Cercosoft' && (
         <tr>
         <td>{filename}</td>
         <td>{filetype}</td>
         <td>{factuurvoorsteldatum}</td>
         <td>
           <button onClick={onDownload} className='btn btn-secondary btn-sm'>
             <i className='icon-cloud-download'></i>{' '}
             <span>{t('general.download')}</span>
           </button>
         </td>
       </tr>
      )}

       {/* PDF Viewer Modal for Afrekeningsborderel documents */}
       {isPdfViewerOpen && pdfData && (
         <PDFViewerModal
           isOpen={isPdfViewerOpen}
           pdfData={pdfData}
           fileName={filename}
           factuurvoorstelnummer={factuurvoorstelnummer}
           annotations={currentAnnotations}
           onClose={handleClosePdfViewer}
           onAnnotationClose={handleAnnotationClose}
         />
       )}
    </Fragment>
  );
};

DocumentItem.propTypes = {
  document: PropTypes.object.isRequired,
  annotations: PropTypes.object, // Map of filename -> array of annotations
};

export default DocumentItem;
